package com.essntl.features.proposal.ui.allproposal.components

import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.essntl.features.proposal.ui.rememberFilteredRequests
import com.essntl.features.request.domain.model.RequestModel
import com.essntl.features.request.domain.repository.RequestRepository
import com.essntl.uikit.components.button.primary.EsLargePrimaryButton
import com.essntl.uikit.components.checkbox.EsCheckBox
import com.essntl.uikit.components.checkbox.EsCheckBoxColors
import com.essntl.uikit.components.dialog.EsDialog
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.icons.EsLargeIcon
import com.essntl.uikit.components.input.EsInput
import com.essntl.uikit.components.spinner.EsSpinner
import com.essntl.uikit.components.spinner.EsSpinnerSize
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import org.koin.compose.koinInject

@Composable
fun SelectRequestPopup(
    isOpen: Boolean,
    onDismissRequest: () -> Unit,
    onConfirmRequest: (String) -> Unit,
    onArrowBackClick: () -> Unit,
) {
    val requestRepository = koinInject<RequestRepository>()

    var searchText by remember { mutableStateOf("") }
    var isLoading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf("") }
    var requests by remember {
        mutableStateOf(emptyList<RequestModel>())
    }

    val filteredRequests = rememberFilteredRequests(requests, searchText)

    var selectedRequestId by remember { mutableStateOf("") }

    LaunchedEffect(isOpen) {
        if (isOpen) {
            isLoading = true
            error = ""
            requests = emptyList()
            selectedRequestId = ""
            searchText = ""
            requestRepository.getAllByStatus(RequestModel.Status.Todo)
                .onSuccess {
                    isLoading = false
                    error = ""
                    requests = it
                }.onFailure {
                    error = "Something went wrong!"
                    isLoading = false
                    requests = emptyList()
                }
        }
    }

    if (isOpen) {
        EsDialog(
            title = "Select request",
            prefix = {
                EsLargeIcon(
                    res = EsIcons.ArrowBack,
                    modifier =
                        Modifier
                            .clip(RoundedCornerShape(1.spacing))
                            .clickable {
                                onDismissRequest()
                                onArrowBackClick()
                            }
                            .padding(0.5.spacing),
                )
            },
            onDismissRequest = onDismissRequest,
        ) {
            Column(
                verticalArrangement = Arrangement.Center,
                horizontalAlignment = Alignment.CenterHorizontally,
                modifier =
                    Modifier
                        .padding(2.5.spacing),
            ) {
                EsInput(
                    value = searchText,
                    onValueChange = {
                        searchText = it
                    },
                    prefix = {
                        EsLargeIcon(res = EsIcons.Search)
                    },
                    placeholder = "Search",
                    shape = RoundedCornerShape(50.dp),
                    modifier =
                        Modifier
                            .fillMaxWidth(),
                )
                Spacer(modifier = Modifier.height(2.spacing))

                LazyColumn(
                    verticalArrangement = Arrangement.spacedBy(1.5.spacing),
                    modifier =
                        Modifier
                            .heightIn(max = 440.dp),
                ) {
                    if (isLoading) {
                        item {
                            Box(
                                contentAlignment = Alignment.Center,
                                modifier = Modifier.fillMaxSize(),
                            ) {
                                EsSpinner(
                                    color = MaterialTheme.esColorScheme.iconPrimary,
                                    size = EsSpinnerSize.Large,
                                    modifier =
                                        Modifier
                                            .align(Alignment.Center),
                                )
                            }
                        }
                    } else if (error.isNotEmpty()) {
                        item {
                            Box(
                                contentAlignment = Alignment.CenterStart,
                                modifier =
                                    Modifier
                                        .fillMaxSize()
                                        .padding(start = 0.5.spacing),
                            ) {
                                Text(
                                    text = error,
                                    textAlign = TextAlign.Start,
                                    style = MaterialTheme.esTypography.p1Regular16,
                                    color = MaterialTheme.esColorScheme.textPrimary,
                                    modifier =
                                        Modifier
                                            .align(Alignment.CenterStart),
                                )
                            }
                        }
                    } else if (filteredRequests.isEmpty()) {
                        item {
                            Box(
                                contentAlignment = Alignment.CenterStart,
                                modifier =
                                    Modifier
                                        .fillMaxSize()
                                        .padding(start = 0.5.spacing),
                            ) {
                                Text(
                                    text = "No requests found!",
                                    textAlign = TextAlign.Start,
                                    style = MaterialTheme.esTypography.p1Regular16,
                                    color = MaterialTheme.esColorScheme.textPrimary,
                                    modifier =
                                        Modifier
                                            .align(Alignment.CenterStart),
                                )
                            }
                        }
                    } else {
                        items(filteredRequests) { request ->
                            key(request.id) {
                                RequestCard(
                                    request = request,
                                    checked = selectedRequestId == request.id,
                                    onCheckedChange = {
                                        selectedRequestId = it
                                    },
                                )
                            }
                        }
                    }
                }
                Spacer(modifier = Modifier.height(2.spacing))
                EsLargePrimaryButton(
                    text = "Confirm",
                    onClick = {
                        onDismissRequest()
                        onConfirmRequest(selectedRequestId)
                    },
                    enabled =
                        requests.isNotEmpty() &&
                            !isLoading &&
                            error.isEmpty() &&
                            selectedRequestId.isNotEmpty(),
                    modifier =
                        Modifier
                            .fillMaxWidth(),
                )
            }
        }
    }
}

@Composable
fun RequestCard(
    request: RequestModel,
    checked: Boolean,
    onCheckedChange: (requestId: String) -> Unit,
) {
    Row(
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
        modifier =
            Modifier.border(
                width = 1.dp,
                color = MaterialTheme.esColorScheme.borderPrimary,
                shape = RoundedCornerShape(2.spacing),
            )
                .clip(RoundedCornerShape(2.spacing))
                .padding(2.spacing)
                .fillMaxWidth(),
    ) {
        Column(
            verticalArrangement = Arrangement.Center,
            horizontalAlignment = Alignment.Start,
        ) {
            Text(
                text = request.title ?: "Title of the request",
                style = MaterialTheme.esTypography.p1Medium16,
                overflow = TextOverflow.Ellipsis,
                maxLines = 1,
            )
            Text(
                text = request.createdAt,
                style =
                    MaterialTheme.esTypography.p2Regular14
                        .copy(color = MaterialTheme.esColorScheme.textSecondary),
                overflow = TextOverflow.Ellipsis,
                maxLines = 1,
            )
        }

        EsCheckBox(
            colors =
                EsCheckBoxColors(
                    backgroundCheckedBoxColor = MaterialTheme.esColorScheme.primary,
                    backgroundUncheckedBoxColor = MaterialTheme.esColorScheme.secondaryHover,
                    borderCheckedBoxColor = Color.Transparent,
                    checkMarkColor = Color.White,
                    disabledCheckedBoxColor = MaterialTheme.esColorScheme.secondaryDisabled,
                    disabledCheckMarkColor = MaterialTheme.esColorScheme.iconDisabled,
                ),
            enabled = true,
            checked = checked,
            onCheckedChange = {
                onCheckedChange(request.id)
            },
        )
    }
}
